import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { OnboardingProvider } from './context/OnboardingContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { resetScrollHold } from './utils/holdScrollPosition';
import SplashScreen from './pages/SplashScreen';
import SignUp from './pages/SignUp';
import CoachSignUp from './pages/CoachSignUp';
import VerifyEmail from './pages/VerifyEmail';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import CopierCode from './pages/CopierCode';
import OnboardingFirstName from './pages/OnboardingFirstName';
import OnboardingBirthDate from './pages/OnboardingBirthDate';
import OnboardingHeight from './pages/OnboardingHeight';
import OnboardingWeight from './pages/OnboardingWeight';
import OnboardingSport from './pages/OnboardingSport';
import OnboardingContraception from './pages/OnboardingContraception';
import OnboardingCycleDates from './pages/OnboardingCycleDates';
import OnboardingSummary from './pages/OnboardingSummary';
import OnboardingNotifications from './pages/OnboardingNotifications';
import OnboardingVerification from './pages/OnboardingVerification';
import AppLayout from './components/AppLayout';
import Home from './pages/Home';
import Cycle from './pages/Cycle';
import Mental from './pages/Mental';
import Sport from './pages/Sport';
import Blog from './pages/Blog';
import Article from './pages/Article';
import Profile from './pages/Profile';
import FormCycle from './pages/FormCycle';
import FormMental from './pages/FormMental';
import FormSport from './pages/FormSport';
import FormConfirmation from './pages/FormConfirmation';
import FormRecap from './pages/FormRecap';
import FormShare from './pages/FormShare';
import HealthConsentWithdrawn from './pages/HealthConsentWithdrawn';
import ConditionsGenerales from './pages/ConditionsGenerales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import ContactSupport from './pages/ContactSupport';
import SupportTicket from './pages/SupportTicket';
import Coach from './pages/Coach';
import CoachSportive from './pages/CoachSportive';
import CoachTeams from './pages/CoachTeams';
import Calendrier from './pages/Calendrier';
import AdminLogin from './pages/AdminLogin';
import Admin from './pages/Admin';
import Blogs from './pages/Blogs';
import SurveyAthletePage from './pages/surveys/SurveyAthletePage';
import SurveyCoachPage from './pages/surveys/SurveyCoachPage';
import NotFound from './pages/NotFound';
import RouteErrorBoundary from './components/ErrorBoundary';

// Chargé à la demande : deux personnes s'en servent, inutile de l'imposer au
// bundle de toutes les utilisatrices.
const GouvernanceApp = lazy(() => import('./pages/gouvernance/GouvernanceApp'));

const SportiveRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role === 'coach') return <Navigate to="/coach" replace />;
  if (user.role === 'editor') return <Navigate to="/blogs" replace />;
  return children;
};

// Saisie de santé : miroir front de `require_health_consent`. Après un retrait du
// consentement, le formulaire laisse place à une explication plutôt que d'échouer
// à l'enregistrement.
const HealthEntryRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.health_consent_withdrawn_at) return <HealthConsentWithdrawn />;
  return children;
};

// Miroir front de `get_current_editor` : le rôle `editor` OU la capacité posée sur
// un compte d'un autre rôle. Tester le seul rôle enfermerait une sportive
// rédactrice hors de l'espace qui lui a été ouvert.
const EditorRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role !== 'editor' && !user.can_edit_blog) return <Navigate to="/connexion" replace />;
  return children;
};

const CoachRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role !== 'coach') return <Navigate to="/accueil" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/admin/connexion" replace />;
  if (user.role !== 'admin') return <Navigate to="/admin/connexion" replace />;
  return children;
};

const PrivateRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/connexion" replace />;
  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  // L'espace ajouté en bas de page pour tenir une position n'a pas à survivre à la page.
  useEffect(() => { resetScrollHold(); window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      {/* Au-dessus des providers : un plantage dans un contexte s'affiche lui aussi
          sur l'écran d'erreur plutôt que sur une page blanche. */}
      <RouteErrorBoundary>
      <AuthProvider>
      <OnboardingProvider>
        <Routes>
          {/* Auth & Onboarding */}
          <Route path="/" element={<SplashScreen />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/inscription" element={<SignUp />} />
          <Route path="/inscription/coach" element={<CoachSignUp />} />
          {/* Saisie du code d'activation : cible du lien envoyé par email, et
              point de rattrapage commun sportive / coach. L'ancienne route coach
              y redirige. */}
          <Route path="/verification" element={<VerifyEmail />} />
          {/* Cible de l'icône 📋 des e-mails à code : copie le code passé en fragment. */}
          <Route path="/copier" element={<CopierCode />} />
          <Route
            path="/inscription/coach/verification"
            element={<Navigate to="/verification" replace />}
          />
          <Route path="/onboarding/prenom" element={<OnboardingFirstName />} />
          <Route path="/onboarding/naissance" element={<OnboardingBirthDate />} />
          <Route path="/onboarding/taille" element={<OnboardingHeight />} />
          <Route path="/onboarding/poids" element={<OnboardingWeight />} />
          <Route path="/onboarding/sport" element={<OnboardingSport />} />
          <Route path="/onboarding/contraception" element={<OnboardingContraception />} />
          <Route path="/onboarding/cycle" element={<OnboardingCycleDates />} />
          <Route path="/onboarding/recapitulatif" element={<OnboardingSummary />} />
          <Route path="/onboarding/notifications" element={<OnboardingNotifications />} />
          <Route path="/onboarding/verification" element={<OnboardingVerification />} />

          {/* App pages (with footer navigation) */}
          <Route element={<AppLayout />}>
            <Route path="/accueil" element={<SportiveRoute><Home /></SportiveRoute>} />
            <Route path="/cycle" element={<SportiveRoute><Cycle /></SportiveRoute>} />
            <Route path="/mental" element={<SportiveRoute><Mental /></SportiveRoute>} />
            <Route path="/sport" element={<SportiveRoute><Sport /></SportiveRoute>} />
            <Route path="/blog" element={<PrivateRoute><Blog /></PrivateRoute>} />
            <Route path="/calendrier" element={<SportiveRoute><Calendrier /></SportiveRoute>} />
            <Route path="/article/:id" element={<PrivateRoute><Article /></PrivateRoute>} />
            <Route path="/profil" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/legal/conditions" element={<ConditionsGenerales />} />
            <Route path="/legal/confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/legal/contact" element={<ContactSupport />} />
            {/* Fil d'un ticket : contrairement aux pages légales voisines, il
                exige une session — il n'existe pas de ticket sans auteur. */}
            <Route path="/support/ticket/:id" element={<PrivateRoute><SupportTicket /></PrivateRoute>} />
            <Route path="/coach" element={<CoachRoute><Coach /></CoachRoute>} />
            <Route path="/coach/equipes" element={<CoachRoute><CoachTeams /></CoachRoute>} />
            <Route path="/coach/sportive/:id" element={<CoachRoute><CoachSportive /></CoachRoute>} />
          </Route>

          <Route path="/formulaire/cycle" element={<SportiveRoute><HealthEntryRoute><FormCycle /></HealthEntryRoute></SportiveRoute>} />
          <Route path="/formulaire/mental" element={<SportiveRoute><HealthEntryRoute><FormMental /></HealthEntryRoute></SportiveRoute>} />
          <Route path="/formulaire/sport" element={<SportiveRoute><HealthEntryRoute><FormSport /></HealthEntryRoute></SportiveRoute>} />
          <Route path="/formulaire/confirmation" element={<SportiveRoute><FormConfirmation /></SportiveRoute>} />
          <Route path="/formulaire/recap" element={<SportiveRoute><FormRecap /></SportiveRoute>} />
          <Route path="/formulaire/share" element={<SportiveRoute><FormShare /></SportiveRoute>} />

          {/* Questionnaires étude quantitative — pages publiques anonymes (aucun
              compte, hors AppLayout). Module découplé : le front appelle forms_api. */}
          <Route path="/questionnaire/sportive" element={<SurveyAthletePage />} />
          <Route path="/questionnaire/coach" element={<SurveyCoachPage />} />

          {/* Admin — pages dédiées, hors AppLayout (pas de barre de navigation) */}
          <Route path="/admin/connexion" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

          {/* Back-office blog — réservé aux comptes éditeur, hors AppLayout */}
          <Route path="/blogs" element={<EditorRoute><Blogs /></EditorRoute>} />

          {/* Gouvernance de l'entreprise — comptes et session propres, sans lien
              avec /admin ni avec les comptes de l'app. */}
          <Route
            path="/gouvernance/*"
            element={(
              <Suspense fallback={null}>
                <GouvernanceApp />
              </Suspense>
            )}
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </OnboardingProvider>
      </AuthProvider>
      </RouteErrorBoundary>
    </Router>
  );
}

export default App;
